// frontend/src/components/dashboard/pos/dialogs/ReceiptDialog.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Divider,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Print, Close } from '@mui/icons-material';
import { format } from 'date-fns';

const ReceiptDialog = ({ open, onClose, order, vendorInfo }) => {
  const { t } = useTranslation();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
  };

  // Handle print functionality
  const handlePrint = () => {
    const receiptContent = document.getElementById('receipt-content');
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>${t('pos.receipt.title')} #${order?.order.order_id || t('common.notAvailable')}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              padding: 20px;
              max-width: 400px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .details {
              margin-bottom: 20px;
            }
            .details-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              padding: 8px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            th {
              font-weight: bold;
            }
            .totals {
              margin-top: 20px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .total-amount {
              font-weight: bold;
              font-size: 1.2rem;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              font-size: 0.8rem;
            }
            @media print {
              body {
                width: 80mm; /* Standard receipt width */
                font-size: 12px;
              }
              button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          ${receiptContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (!order) return null;

  // Calculate totals
  const subtotal = order.items.reduce((total, item) => total + (item.price * item.qty), 0);
  const discount = order.order.discount || 0;
  const total = order.order.total_amount;
  const tax = total - subtotal + discount;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee'
        }}
      >
        <Typography variant="h6">{t('pos.receipt.title')}</Typography>
        <Box>
          <Button
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            {t('pos.receipt.print')}
          </Button>
          <Button
            startIcon={<Close />}
            onClick={onClose}
          >
            {t('pos.receipt.close')}
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box id="receipt-content" sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {vendorInfo?.vendorName || t('company.name')}
            </Typography>
            <Typography variant="body1">
              {order.branch?.location_name || t('pos.receipt.branchName')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.branch?.address || t('pos.receipt.address')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.branch?.contact_number || t('pos.receipt.contactNumber')}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Order Details */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {t('pos.receipt.orderDetails')}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('pos.receipt.receiptNumber')}:
              </Typography>
              <Typography variant="body2">
                {order.order.order_id}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('pos.receipt.dateTime')}:
              </Typography>
              <Typography variant="body2">
                {formatDate(order.order.created_at)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('pos.receipt.cashier')}:
              </Typography>
              <Typography variant="body2">
                {order.account.username}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('pos.receipt.paymentMethod')}:
              </Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {order.payment?.method || t('common.notAvailable')}
              </Typography>
            </Box>
            
            {order.order.device_number && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('pos.deviceNumber')}:
                </Typography>
                <Typography variant="body2">
                  {order.order.device_number}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Order Items */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('pos.receipt.orderItems')}
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('pos.receipt.item')}</TableCell>
                  <TableCell align="center">{t('pos.receipt.qty')}</TableCell>
                  <TableCell align="right">{t('pos.receipt.price')}</TableCell>
                  <TableCell align="right">{t('pos.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.map((item) => (
                  <TableRow key={item.order_item_id}>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell align="center">{item.qty}</TableCell>
                    <TableCell align="right">${parseFloat(item.price).toFixed(2)}</TableCell>
                    <TableCell align="right">${(item.price * item.qty).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Totals */}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: 200 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">
                  {t('pos.subtotal')}:
                </Typography>
                <Typography variant="body2">
                  ${subtotal.toFixed(2)}
                </Typography>
              </Box>
              
              {tax > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {t('pos.receipt.taxService')}:
                  </Typography>
                  <Typography variant="body2">
                    ${tax.toFixed(2)}
                  </Typography>
                </Box>
              )}
              
              {discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {t('pos.receipt.discount')}:
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    -${discount.toFixed(2)}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('pos.total')}:
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  ${parseFloat(total).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('pos.receipt.thankYouMessage')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('pos.receipt.systemName')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptDialog;